/**
 * MEDIA-EDITOR-PROPERTY-SURFACES-A1 — freeform / original-aspect crop geometry.
 * Pure model: no React, DOM, or File I/O.
 */

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 3;
export const INITIAL_ZOOM = 1;
export const NUDGE_PX = 4;
export const NUDGE_SHIFT_PX = 12;
export const MIN_FREE_CROP_SIDE = 32;

export const CROP_MODE_FULL = "full";
export const CROP_MODE_FREE = "free";

export function isValidSourceSize(sourceWidth, sourceHeight) {
  return (
    Number.isFinite(sourceWidth)
    && Number.isFinite(sourceHeight)
    && sourceWidth > 0
    && sourceHeight > 0
  );
}

export function isValidViewportSize(viewportWidth, viewportHeight) {
  return (
    Number.isFinite(viewportWidth)
    && Number.isFinite(viewportHeight)
    && viewportWidth > 0
    && viewportHeight > 0
  );
}

/** Fit entire image inside viewport at zoom=1 (contain, not cover). */
export function computeFitScale(sourceWidth, sourceHeight, viewportWidth, viewportHeight) {
  if (
    !isValidSourceSize(sourceWidth, sourceHeight)
    || !isValidViewportSize(viewportWidth, viewportHeight)
  ) {
    return null;
  }
  return Math.min(
    viewportWidth / sourceWidth,
    viewportHeight / sourceHeight,
  );
}

export function clampZoom(zoom) {
  if (!Number.isFinite(zoom)) return INITIAL_ZOOM;
  if (zoom < MIN_ZOOM) return MIN_ZOOM;
  if (zoom > MAX_ZOOM) return MAX_ZOOM;
  return zoom;
}

export function computeEffectiveScale(
  sourceWidth,
  sourceHeight,
  viewportWidth,
  viewportHeight,
  zoom,
) {
  const base = computeFitScale(sourceWidth, sourceHeight, viewportWidth, viewportHeight);
  if (base == null) return null;
  return base * clampZoom(zoom);
}

export function computeMaxOffsets(
  sourceWidth,
  sourceHeight,
  viewportWidth,
  viewportHeight,
  zoom,
) {
  const effectiveScale = computeEffectiveScale(
    sourceWidth,
    sourceHeight,
    viewportWidth,
    viewportHeight,
    zoom,
  );
  if (effectiveScale == null) {
    return { maxOffsetX: 0, maxOffsetY: 0, effectiveScale: null };
  }
  const renderedWidth = sourceWidth * effectiveScale;
  const renderedHeight = sourceHeight * effectiveScale;
  return {
    maxOffsetX: Math.max(0, (renderedWidth - viewportWidth) / 2),
    maxOffsetY: Math.max(0, (renderedHeight - viewportHeight) / 2),
    effectiveScale,
  };
}

export function clampOffset(
  offsetX,
  offsetY,
  sourceWidth,
  sourceHeight,
  viewportWidth,
  viewportHeight,
  zoom,
) {
  const { maxOffsetX, maxOffsetY } = computeMaxOffsets(
    sourceWidth,
    sourceHeight,
    viewportWidth,
    viewportHeight,
    zoom,
  );
  const x = Number.isFinite(offsetX) ? offsetX : 0;
  const y = Number.isFinite(offsetY) ? offsetY : 0;
  return {
    offsetX: Math.min(maxOffsetX, Math.max(-maxOffsetX, x)),
    offsetY: Math.min(maxOffsetY, Math.max(-maxOffsetY, y)),
  };
}

/**
 * Source-space crop rectangle from the visible viewport transform.
 * At fit zoom with zero offset the full source frame is returned.
 */
export function getSourceCropRect(
  sourceWidth,
  sourceHeight,
  viewportWidth,
  viewportHeight,
  zoom,
  offsetX,
  offsetY,
) {
  const effectiveScale = computeEffectiveScale(
    sourceWidth,
    sourceHeight,
    viewportWidth,
    viewportHeight,
    zoom,
  );
  if (effectiveScale == null) return null;

  const clamped = clampOffset(
    offsetX,
    offsetY,
    sourceWidth,
    sourceHeight,
    viewportWidth,
    viewportHeight,
    zoom,
  );

  const sourceCropWidth = viewportWidth / effectiveScale;
  const sourceCropHeight = viewportHeight / effectiveScale;
  const sourceCenterX = sourceWidth / 2 - clamped.offsetX / effectiveScale;
  const sourceCenterY = sourceHeight / 2 - clamped.offsetY / effectiveScale;

  let left = sourceCenterX - sourceCropWidth / 2;
  let top = sourceCenterY - sourceCropHeight / 2;

  if (left < 0) left = 0;
  if (top < 0) top = 0;
  if (left + sourceCropWidth > sourceWidth) left = sourceWidth - sourceCropWidth;
  if (top + sourceCropHeight > sourceHeight) top = sourceHeight - sourceCropHeight;

  left = Math.max(0, Math.min(left, sourceWidth - sourceCropWidth));
  top = Math.max(0, Math.min(top, sourceHeight - sourceCropHeight));

  const width = Math.min(sourceCropWidth, sourceWidth);
  const height = Math.min(sourceCropHeight, sourceHeight);

  if (!(width > 0) || !(height > 0)) return null;

  return {
    x: left,
    y: top,
    width,
    height,
  };
}

export function clampFreeCropRect(
  cropX,
  cropY,
  cropWidth,
  cropHeight,
  sourceWidth,
  sourceHeight,
) {
  if (!(sourceWidth > 0) || !(sourceHeight > 0)) {
    return { cropX: 0, cropY: 0, cropWidth: 0, cropHeight: 0 };
  }
  const minW = Math.min(MIN_FREE_CROP_SIDE, sourceWidth);
  const minH = Math.min(MIN_FREE_CROP_SIDE, sourceHeight);
  let w = Number.isFinite(cropWidth) ? cropWidth : minW;
  let h = Number.isFinite(cropHeight) ? cropHeight : minH;
  w = Math.max(minW, Math.min(w, sourceWidth));
  h = Math.max(minH, Math.min(h, sourceHeight));
  let x = Number.isFinite(cropX) ? cropX : 0;
  let y = Number.isFinite(cropY) ? cropY : 0;

  if (x < 0) x = 0;
  if (y < 0) y = 0;
  if (x + w > sourceWidth) x = sourceWidth - w;
  if (y + h > sourceHeight) y = sourceHeight - h;
  x = Math.max(0, Math.min(x, sourceWidth - w));
  y = Math.max(0, Math.min(y, sourceHeight - h));

  return { cropX: x, cropY: y, cropWidth: w, cropHeight: h };
}

export function createInitialCropState(
  sourceWidth,
  sourceHeight,
  viewportWidth,
  viewportHeight,
) {
  if (
    !isValidSourceSize(sourceWidth, sourceHeight)
    || !isValidViewportSize(viewportWidth, viewportHeight)
  ) {
    return null;
  }
  return {
    mode: CROP_MODE_FULL,
    zoom: INITIAL_ZOOM,
    offsetX: 0,
    offsetY: 0,
    rotation: 0,
    cropX: 0,
    cropY: 0,
    cropWidth: sourceWidth,
    cropHeight: sourceHeight,
    naturalWidth: sourceWidth,
    naturalHeight: sourceHeight,
    sourceWidth,
    sourceHeight,
    viewportWidth,
    viewportHeight,
  };
}

export function withCropMode(state, mode) {
  if (!state || mode === state.mode) return state;
  if (mode === CROP_MODE_FREE) {
    const minW = Math.min(MIN_FREE_CROP_SIDE, state.sourceWidth);
    const minH = Math.min(MIN_FREE_CROP_SIDE, state.sourceHeight);
    const w = Math.max(minW, Math.round(state.sourceWidth * 0.75));
    const h = Math.max(minH, Math.round(state.sourceHeight * 0.45));
    const clamped = clampFreeCropRect(
      (state.sourceWidth - w) / 2,
      (state.sourceHeight - h) / 2,
      w,
      h,
      state.sourceWidth,
      state.sourceHeight,
    );
    return {
      ...state,
      mode: CROP_MODE_FREE,
      ...clamped,
    };
  }
  return {
    ...createInitialCropState(
      state.sourceWidth,
      state.sourceHeight,
      state.viewportWidth,
      state.viewportHeight,
    ),
    rotation: state.rotation,
    naturalWidth: state.naturalWidth || state.sourceWidth,
    naturalHeight: state.naturalHeight || state.sourceHeight,
  };
}

export function getExportCropRect(state) {
  if (!state) return null;
  if (state.mode === CROP_MODE_FREE) {
    const clamped = clampFreeCropRect(
      state.cropX,
      state.cropY,
      state.cropWidth,
      state.cropHeight,
      state.sourceWidth,
      state.sourceHeight,
    );
    return {
      x: clamped.cropX,
      y: clamped.cropY,
      width: clamped.cropWidth,
      height: clamped.cropHeight,
    };
  }
  return getSourceCropRect(
    state.sourceWidth,
    state.sourceHeight,
    state.viewportWidth,
    state.viewportHeight,
    state.zoom,
    state.offsetX,
    state.offsetY,
  );
}

export function nudgeFreeCrop(state, dx, dy) {
  if (!state || state.mode !== CROP_MODE_FREE) return state;
  const next = clampFreeCropRect(
    state.cropX + dx,
    state.cropY + dy,
    state.cropWidth,
    state.cropHeight,
    state.sourceWidth,
    state.sourceHeight,
  );
  return { ...state, ...next };
}

export function resizeFreeCrop(state, deltaWidth, deltaHeight, handle = "se") {
  if (!state || state.mode !== CROP_MODE_FREE) return state;
  const W = state.sourceWidth;
  const H = state.sourceHeight;
  const minW = Math.min(MIN_FREE_CROP_SIDE, W);
  const minH = Math.min(MIN_FREE_CROP_SIDE, H);
  let { cropX: x, cropY: y, cropWidth: w, cropHeight: h } = state;

  const hName = String(handle || "se").toLowerCase();
  const fromWest = hName.includes("w");
  const fromNorth = hName.includes("n");
  const fromEast = hName.includes("e");
  const fromSouth = hName.includes("s");

  if (fromEast) {
    w = Math.max(minW, Math.min(w + deltaWidth, W - x));
  } else if (fromWest) {
    const right = x + w;
    const nextW = Math.max(minW, Math.min(w - deltaWidth, right));
    x = right - nextW;
    if (x < 0) {
      w = right;
      x = 0;
    } else {
      w = nextW;
    }
    w = Math.max(minW, w);
  }

  if (fromSouth) {
    h = Math.max(minH, Math.min(h + deltaHeight, H - y));
  } else if (fromNorth) {
    const bottom = y + h;
    const nextH = Math.max(minH, Math.min(h - deltaHeight, bottom));
    y = bottom - nextH;
    if (y < 0) {
      h = bottom;
      y = 0;
    } else {
      h = nextH;
    }
    h = Math.max(minH, h);
  }

  // If only width/height deltas without N/W (legacy se path), grow SE.
  if (!fromWest && !fromEast && deltaWidth) {
    w = Math.max(minW, Math.min(w + deltaWidth, W - x));
  }
  if (!fromNorth && !fromSouth && deltaHeight) {
    h = Math.max(minH, Math.min(h + deltaHeight, H - y));
  }

  const next = clampFreeCropRect(x, y, w, h, W, H);
  return { ...state, ...next };
}

export function freeCropToViewportRect(state) {
  if (!state || state.mode !== CROP_MODE_FREE) return null;
  const scale = computeFitScale(
    state.sourceWidth,
    state.sourceHeight,
    state.viewportWidth,
    state.viewportHeight,
  );
  if (scale == null) return null;
  const renderedWidth = state.sourceWidth * scale;
  const renderedHeight = state.sourceHeight * scale;
  const offsetX = (state.viewportWidth - renderedWidth) / 2;
  const offsetY = (state.viewportHeight - renderedHeight) / 2;
  return {
    left: offsetX + state.cropX * scale,
    top: offsetY + state.cropY * scale,
    width: state.cropWidth * scale,
    height: state.cropHeight * scale,
    scale,
    offsetX,
    offsetY,
  };
}

/** Preferred CSS px hit target for crop corner handles (visual marker may be smaller). */
export const CROP_HANDLE_HIT_PX = 44;

const CROP_HANDLE_ORDER = ["nw", "ne", "sw", "se"];

/**
 * Deterministic nearest-corner handle ownership in client coordinates.
 * When opposite 44px targets would overlap on a small crop, the nearest corner
 * wins — never DOM paint order.
 *
 * @returns {"nw"|"ne"|"sw"|"se"|null}
 */
export function resolveNearestCropHandle(
  clientX,
  clientY,
  viewportClientRect,
  cropViewportRect,
  hitPx = CROP_HANDLE_HIT_PX,
) {
  if (!viewportClientRect || !cropViewportRect) return null;
  const left = viewportClientRect.left + cropViewportRect.left;
  const top = viewportClientRect.top + cropViewportRect.top;
  const right = left + cropViewportRect.width;
  const bottom = top + cropViewportRect.height;
  const corners = {
    nw: { x: left, y: top },
    ne: { x: right, y: top },
    sw: { x: left, y: bottom },
    se: { x: right, y: bottom },
  };
  const radius = hitPx / 2;
  let best = null;
  let bestDist = Infinity;
  CROP_HANDLE_ORDER.forEach((handle) => {
    const corner = corners[handle];
    const dist = Math.hypot(clientX - corner.x, clientY - corner.y);
    if (dist <= radius && dist < bestDist) {
      bestDist = dist;
      best = handle;
    }
  });
  return best;
}

/**
 * Transform free-crop rectangle with image rotation.
 * deltaDegrees: +90 = clockwise, -90 = counter-clockwise.
 */
export function rotateFreeCropRect(rect, sourceWidth, sourceHeight, deltaDegrees) {
  const x = rect.cropX ?? rect.x;
  const y = rect.cropY ?? rect.y;
  const w = rect.cropWidth ?? rect.width;
  const h = rect.cropHeight ?? rect.height;
  const delta = ((Number(deltaDegrees) % 360) + 360) % 360;
  const W = sourceWidth;
  const H = sourceHeight;

  if (delta === 90) {
    // Clockwise 90°
    return {
      cropX: H - (y + h),
      cropY: x,
      cropWidth: h,
      cropHeight: w,
    };
  }
  if (delta === 270) {
    // Counter-clockwise 90° (same as +270)
    return {
      cropX: y,
      cropY: W - (x + w),
      cropWidth: h,
      cropHeight: w,
    };
  }
  if (delta === 180) {
    return {
      cropX: W - (x + w),
      cropY: H - (y + h),
      cropWidth: w,
      cropHeight: h,
    };
  }
  return { cropX: x, cropY: y, cropWidth: w, cropHeight: h };
}

export function withRotation(state, deltaDegrees) {
  if (!state) return null;
  const delta = Number(deltaDegrees);
  if (!Number.isFinite(delta) || delta === 0) return state;
  const nextRotation = (((state.rotation || 0) + delta) % 360 + 360) % 360;
  const normalized =
    nextRotation === 90 || nextRotation === 180 || nextRotation === 270
      ? nextRotation
      : 0;
  const swap = Math.abs(delta) % 180 === 90;
  const sourceWidth = swap ? state.sourceHeight : state.sourceWidth;
  const sourceHeight = swap ? state.sourceWidth : state.sourceHeight;
  const next = createInitialCropState(
    sourceWidth,
    sourceHeight,
    state.viewportWidth,
    state.viewportHeight,
  );
  if (!next) return null;

  if (state.mode === CROP_MODE_FREE) {
    const mapped = rotateFreeCropRect(
      {
        cropX: state.cropX,
        cropY: state.cropY,
        cropWidth: state.cropWidth,
        cropHeight: state.cropHeight,
      },
      state.sourceWidth,
      state.sourceHeight,
      delta,
    );
    const clamped = clampFreeCropRect(
      mapped.cropX,
      mapped.cropY,
      mapped.cropWidth,
      mapped.cropHeight,
      sourceWidth,
      sourceHeight,
    );
    return {
      ...next,
      mode: CROP_MODE_FREE,
      rotation: normalized,
      naturalWidth: state.naturalWidth || state.sourceWidth,
      naturalHeight: state.naturalHeight || state.sourceHeight,
      sourceWidth,
      sourceHeight,
      ...clamped,
    };
  }
  return {
    ...next,
    mode: CROP_MODE_FULL,
    rotation: normalized,
    naturalWidth: state.naturalWidth || state.sourceWidth,
    naturalHeight: state.naturalHeight || state.sourceHeight,
    sourceWidth,
    sourceHeight,
  };
}

export function withZoom(state, nextZoom) {
  if (!state) return null;
  const zoom = clampZoom(nextZoom);
  const clamped = clampOffset(
    state.offsetX,
    state.offsetY,
    state.sourceWidth,
    state.sourceHeight,
    state.viewportWidth,
    state.viewportHeight,
    zoom,
  );
  return {
    ...state,
    zoom,
    offsetX: clamped.offsetX,
    offsetY: clamped.offsetY,
  };
}

export function nudgeCrop(state, dx, dy) {
  if (!state) return null;
  if (state.mode === CROP_MODE_FREE) {
    return nudgeFreeCrop(state, dx, dy);
  }
  return {
    ...state,
    ...clampOffset(
      state.offsetX + dx,
      state.offsetY + dy,
      state.sourceWidth,
      state.sourceHeight,
      state.viewportWidth,
      state.viewportHeight,
      state.zoom,
    ),
  };
}

export function panCrop(state, dx, dy) {
  return nudgeCrop(state, dx, dy);
}

export function nudgeAmountForKeyboard(shiftKey) {
  return shiftKey ? NUDGE_SHIFT_PX : NUDGE_PX;
}

export function isCropKeyboardTarget(element) {
  if (!element) return false;
  const tag = element.tagName?.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") {
    return false;
  }
  if (element.dataset?.propertyImageEditor === "crop-handle") {
    return true;
  }
  if (tag === "button") {
    return false;
  }
  return (
    element.dataset?.propertyImageEditor === "viewport"
    || element.dataset?.propertyImageEditor === "crop-surface"
  );
}
